import {Avatar, Modal} from 'antd'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import React from 'react'
import {BsCalendar3, BsGenderAmbiguous, BsPerson, BsTelephone} from 'react-icons/bs'
import {FaUserGraduate} from 'react-icons/fa6'
import {MdChurch, MdLocationOn, MdOutlineEmail} from 'react-icons/md'
import {PiCross, PiIdentificationCard} from 'react-icons/pi'
import {IdentifierTypes} from 'views/AppLocals/appFieldTypes'
import _ from 'lodash'

const GenderLabels = {0: 'unknown', 1: 'male', 2: 'female', 3: 'other'}

const Row = ({icon, label, value, mono = false}) => {
    if (!value) return null
    return (
        <div className="flex items-start gap-2 py-1.5 border-b border-color-50 last:border-b-0">
            <span className="flex-shrink-0 mt-0.5 text-color-300">{icon}</span>
            <div className="flex flex-col min-w-0 flex-1">
                <span className="text-2xs text-color-400 uppercase tracking-wider leading-tight">{label}</span>
                <span className={classNames(
                    'text-sm leading-snug break-words',
                    mono ? 'font-mono text-blue-500' : 'text-color-100'
                )}>
                    {value}
                </span>
            </div>
        </div>
    )
}

const Section = ({title, children}) => (
    <div className="flex flex-col gap-0 pt-3">
        <span className="text-2xs font-semibold text-color-400 uppercase tracking-widest pb-1.5 border-b border-color-50">
            {title}
        </span>
        {children}
    </div>
)

export const StudentDetailModal = ({student, open, onClose}) => {
    const t = useTranslate()

    if (!student) return null

    const identifierLabel = _.find(IdentifierTypes, {value: student.identifier_type})?.label
    const yearOfBirth = student.date_of_birth
        ? new Date(student.date_of_birth).getFullYear()
        : null
    const age = yearOfBirth ? new Date().getFullYear() - yearOfBirth : null

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={null}
            width={420}
            styles={{body: {padding: 0}}}>
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-color-50">
                <Avatar
                    size={52}
                    src={student.avatar}
                    icon={<FaUserGraduate size={22} className="text-color-400"/>}
                    className="flex-shrink-0 background-200"
                />
                <div className="flex flex-col gap-0.5 min-w-0">
                    {student.patron_saint_name && (
                        <span className="text-sm font-medium text-purple-500 italic leading-tight">
                            {student.patron_saint_name}
                        </span>
                    )}
                    <span className="font-bold text-base text-color-000 leading-snug">
                        {student.fullname}
                    </span>
                    {student.student_idcode && (
                        <span className="text-xs font-mono text-blue-500">
                            #{student.student_idcode}
                        </span>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-4 flex flex-col gap-0 overflow-y-auto max-h-96">
                <Section title={t('personal information')}>
                    <Row
                        icon={<PiCross size={13}/>}
                        label={t('patron saint')}
                        value={student.patron_saint_name}
                    />
                    <Row
                        icon={<BsCalendar3 size={13}/>}
                        label={t('date of birth')}
                        value={yearOfBirth
                            ? (age ? `${yearOfBirth} (${age} ${t('years old')})` : String(yearOfBirth))
                            : null
                        }
                    />
                    <Row
                        icon={<BsGenderAmbiguous size={13}/>}
                        label={t('gender')}
                        value={t(GenderLabels[student.gender] ?? 'unknown')}
                    />
                    {student.identifier_code && (
                        <Row
                            icon={<PiIdentificationCard size={13}/>}
                            label={t(identifierLabel ?? 'identifier')}
                            value={student.identifier_code}
                        />
                    )}
                </Section>

                <Section title={t('contact')}>
                    <Row
                        icon={<BsTelephone size={13}/>}
                        label={t('phone')}
                        value={student.contact_phone}
                    />
                    <Row
                        icon={<MdOutlineEmail size={13}/>}
                        label={t('email')}
                        value={student.contact_email}
                    />
                </Section>

                {(student.parish_name || student.diocese_name) && (
                    <Section title={t('parish')}>
                        <Row
                            icon={<MdChurch size={13}/>}
                            label={t('belong to parish')}
                            value={student.parish_name}
                        />
                        <Row
                            icon={<MdLocationOn size={13}/>}
                            label={t('diocese')}
                            value={student.diocese_name}
                        />
                    </Section>
                )}

                <Section title={t('system')}>
                    <Row
                        icon={<BsPerson size={13}/>}
                        label={t('student id')}
                        value={student.student_idcode}
                        mono={true}
                    />
                </Section>
            </div>
        </Modal>
    )
}
